/*
  OBSIDIAN — Motion layer JS (Workstream A)
  =========================================
  Tiny, dependency-free, native-first. Three responsibilities, each guarded so a
  reduced-motion user (or a browser without the feature) lands on the static
  design with no motion and no broken state:

    1. <masked-reveal>            signature line-by-line hero type reveal on load,
                                  with a first-visit sessionStorage gate.
    2. scroll-reveal observer     adds `.ch-reveal--in` (+ `.is-in`) once, then
                                  unobserves. Extends Dawn's scroll-trigger idea
                                  (IntersectionObserver, reveal-once) onto the
                                  Obsidian `.ch-reveal` targets — it does NOT
                                  touch Dawn's own `.scroll-trigger` elements.
                                  Stagger delays cap at REVEAL_STAGGER_CAP slots
                                  so deep grids never queue seconds of delay.
                                  A MutationObserver re-scans content injected
                                  after boot (facet re-renders, pagination,
                                  product recommendations) so late .ch-reveal
                                  nodes still reveal instead of staying hidden.
    3. view-transition JIT naming assigns a shared `view-transition-name` to the
                                  clicked product card's media just before nav so
                                  the browser morphs it into the PDP hero, then
                                  clears it on arrival.
    4. viewport animation gate    toggles `.ch-anim-offscreen` on any
                                  [data-anim-pause] element while it is outside
                                  the viewport, so continuous decorative loops
                                  (ticker/marquee tracks, hotspot pulses) pause
                                  their animation-play-state instead of running
                                  forever below the fold. Sections opt in with
                                  the attribute and their own paused-state CSS.

  Public API surface (see obsidian-motion.css header for the matching classes):
    custom element : <masked-reveal>   [data-gate="session"] optional
    reveal target  : .ch-reveal         [data-reveal-order], [data-reveal-group]
    card hook      : [data-vt-card]      on the product-card link (S adds it)
    pdp hook       : [data-vt-hero]      on the PDP hero media    (S adds it)
    pause hook     : [data-anim-pause]   gets .ch-anim-offscreen out of view
*/

(() => {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const prefersReduced = () => REDUCED_MOTION.matches;

  /* ================================================================== *
   * 0. Motion gate — fail-safe hook for the CSS
   * ------------------------------------------------------------------ *
   * The CSS default (no class present) is the fully-visible static
   * end-state for every reveal. A hidden "start" state (opacity:0,
   * clip curtain, masked-reveal translateY) is only ever applied by the
   * CSS when `html.js-motion` is present AND the user allows motion
   * (`@media (prefers-reduced-motion: no-preference)` wraps it there).
   * We add the class synchronously, first thing, before any other work,
   * so there's no window where a hidden-start rule could apply without
   * this hook already being in place. If JS never runs at all (disabled,
   * blocked, errors before this line), the class never appears and the
   * CSS default (visible) is what renders — no trap possible.
   * ================================================================== */
  if (!prefersReduced()) {
    document.documentElement.classList.add('js-motion');
  }

  /* Per-line stagger for the hero reveal, in ms. Mirrors --ch-stagger-reveal.
     Athletic tempo: tighter than the old luxury 100ms. */
  const HERO_STAGGER_MS = 60;
  /* The shared name both the grid thumb and the PDP hero adopt for the morph. */
  const VT_NAME = 'ch-hero-media';

  /* ================================================================== *
   * 1. <masked-reveal> — line-by-line hero type reveal
   * ------------------------------------------------------------------ *
   * Wrap each child line in an overflow:hidden mask, then play the rise
   * with a staggered delay. Space is reserved by the CSS mask, so there
   * is no layout shift while lines are held below the mask.
   *
   * Usage (Workstream S):
   *   <masked-reveal data-gate="session">
   *     <span class="masked-reveal__line">OBSIDIAN</span>
   *     <span class="masked-reveal__line">DROP 01</span>
   *   </masked-reveal>
   * If S provides bare lines, we still wrap the inner text so the mask works.
   * ================================================================== */
  class MaskedReveal extends HTMLElement {
    connectedCallback() {
      // Reduced motion / first-visit gate: show text immediately, statically.
      if (prefersReduced() || this.#gatedOut()) {
        this.#revealStatic();
        return;
      }

      try {
        this.#prepareLines();
        // Play after paint so the initial hidden state is committed (no flash).
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.classList.add('masked-reveal--in'));
        });
        this.#markSeen();
      } catch (err) {
        // Anything above throwing must not leave the heading stuck behind
        // the html.js-motion hidden-start state — fall back to static-visible.
        this.#revealStatic();
        throw err;
      }
    }

    /** @returns {boolean} true when a session gate has already fired this visit */
    #gatedOut() {
      if (this.dataset.gate !== 'session') return false;
      try {
        return sessionStorage.getItem('ch-hero-overture') === '1';
      } catch {
        return false;
      }
    }

    #markSeen() {
      if (this.dataset.gate !== 'session') return;
      try {
        sessionStorage.setItem('ch-hero-overture', '1');
      } catch {
        /* storage blocked (private mode) — reveal still ran, just not gated */
      }
    }

    /** Ensure every line has an inner riser element the CSS animates. */
    #prepareLines() {
      const lines = this.querySelectorAll('.masked-reveal__line');
      const rows = lines.length ? lines : [this];
      rows.forEach((line, index) => {
        if (!line.querySelector('.masked-reveal__inner')) {
          const inner = document.createElement('span');
          inner.className = 'masked-reveal__inner';
          while (line.firstChild) inner.appendChild(line.firstChild);
          line.appendChild(inner);
        }
        const inner = line.querySelector('.masked-reveal__inner');
        inner.style.setProperty('--masked-reveal-delay', `${index * HERO_STAGGER_MS}ms`);
      });
    }

    /** Reduced-motion / gated path: guarantee the copy is fully visible. */
    #revealStatic() {
      this.classList.add('masked-reveal--in');
      this.querySelectorAll('.masked-reveal__inner').forEach((inner) => {
        inner.style.transform = 'none';
        inner.style.animation = 'none';
      });
    }
  }

  if (!customElements.get('masked-reveal')) {
    customElements.define('masked-reveal', MaskedReveal);
  }

  /* ================================================================== *
   * 2. Scroll reveal — reveal-once IntersectionObserver on .ch-reveal
   * ------------------------------------------------------------------ *
   * Same shape as Dawn's scroll-trigger (observe, flip a class on entry,
   * unobserve) but scoped to Obsidian's own targets so the two systems
   * never fight. Per-item stagger comes from [data-reveal-order] (or DOM
   * order within a [data-reveal-group]).
   * ================================================================== */
  const REVEAL_SELECTOR = '.ch-reveal';
  /* Stagger slots are capped so deep grids never queue long delays: an item
     with [data-reveal-order] beyond the cap shares the last slot and reveals
     together with its peers (max delay = cap * HERO_STAGGER_MS = 300ms). */
  const REVEAL_STAGGER_CAP = 5;

  function playReveal(el) {
    const order = Math.min(Number(el.dataset.revealOrder ?? 0) || 0, REVEAL_STAGGER_CAP);
    el.style.setProperty('--ch-reveal-delay', `${order * HERO_STAGGER_MS}ms`);
    el.classList.add('ch-reveal--in', 'is-in');
  }

  /* One shared observer for every reveal target, created lazily. A single
     instance makes repeat scans (facet re-renders, recommendations, the
     MutationObserver below) idempotent: observing an already-observed node
     is a spec-level no-op, so double scans cannot double-fire. */
  let revealObserver = null;

  function getRevealObserver() {
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            playReveal(entry.target);
            obs.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
      );
    }
    return revealObserver;
  }

  function initScrollReveals(root = document) {
    const targets = Array.from(root.querySelectorAll(REVEAL_SELECTOR));
    // Include the root itself when it is a reveal target (querySelectorAll
    // only looks at descendants) — injected nodes are often the target.
    if (root instanceof Element && root.matches(REVEAL_SELECTOR)) targets.unshift(root);
    const fresh = targets.filter((el) => !el.classList.contains('is-in'));
    if (fresh.length === 0) return;

    // Reduced motion (or no IO support): mark everything in, statically.
    if (prefersReduced() || !('IntersectionObserver' in window)) {
      fresh.forEach((el) => el.classList.add('ch-reveal--in', 'is-in'));
      return;
    }

    // Auto-assign stagger order within a shared group when not set explicitly.
    const groups = new Map();
    for (const el of fresh) {
      if (el.dataset.revealOrder != null) continue;
      const key = el.dataset.revealGroup ?? '';
      const next = groups.get(key) ?? 0;
      el.dataset.revealOrder = String(next);
      groups.set(key, next + 1);
    }

    const observer = getRevealObserver();
    for (const el of fresh) observer.observe(el);
  }

  /* Facet filtering / pagination (facets.js swaps #ProductGridContainer via
     the Section Rendering API) and product recommendations inject .ch-reveal
     nodes long after the initial scan. Under html.js-motion those nodes start
     hidden, and in browsers without scroll-driven animations nothing would
     ever reveal them — so watch the document for injected targets and hand
     them to the shared observer. Attribute/class churn is ignored (childList
     only), and initScrollReveals skips anything already revealed. */
  function watchInjectedReveals() {
    if (!('MutationObserver' in window) || !document.body) return;
    // Without the js-motion gate nothing is ever hidden; no re-scan needed.
    if (!document.documentElement.classList.contains('js-motion')) return;

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if (node.matches(REVEAL_SELECTOR) || node.querySelector(REVEAL_SELECTOR)) {
            initScrollReveals(node);
          }
        }
      }
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  /* ================================================================== *
   * 3. View Transitions — JIT shared-element naming on the clicked card
   * ------------------------------------------------------------------ *
   * Cross-document (MPA) View Transitions fire a `pageswap` on the way out
   * and `pagereveal` on arrival. We tag ONLY the clicked card's media with
   * the shared VT_NAME on the way out, and the PDP hero with the same name
   * on arrival, so exactly one pair morphs. Everything else crossfades.
   *
   * Progressive enhancement: if the API is absent, links behave normally.
   * Gated behind reduced-motion.
   * ================================================================== */
  const VT_SUPPORTED =
    'startViewTransition' in document ||
    // Cross-document VT is detected via the pageswap/pagereveal events.
    'onpageswap' in window;

  let pendingCardName = null;

  function clearNamed(name) {
    document.querySelectorAll(`[style*="view-transition-name: ${name}"]`).forEach((el) => {
      el.style.viewTransitionName = '';
    });
  }

  function initViewTransitions() {
    if (!VT_SUPPORTED || prefersReduced()) return;

    // On click, remember which card's media should morph. Use pointerdown so the
    // name is set before the browser snapshots the outgoing page on pageswap.
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest?.('a[href]');
        if (!link) return;
        const card = link.closest('[data-vt-card]') || (link.matches('[data-vt-card]') ? link : null);
        if (!card) {
          pendingCardName = null;
          return;
        }
        const media = card.querySelector('[data-vt-media]') || card;
        clearNamed(VT_NAME);
        media.style.viewTransitionName = VT_NAME;
        pendingCardName = VT_NAME;
      },
      { capture: true }
    );

    // Outgoing snapshot: keep the name only for a real same-origin navigation.
    window.addEventListener('pageswap', (event) => {
      const nav = event.activation?.entry?.url;
      if (!nav || !pendingCardName) return;
      // Name is already on the element from the click handler; nothing else to do.
    });

    // Incoming page: tag the PDP hero so it pairs with the outgoing card media.
    window.addEventListener('pagereveal', (event) => {
      if (!event.viewTransition) return;
      const hero = document.querySelector('[data-vt-hero]');
      if (hero) hero.style.viewTransitionName = VT_NAME;
      // Clean up once the transition settles so the name is free for next nav.
      event.viewTransition.finished.finally(() => {
        if (hero) hero.style.viewTransitionName = '';
      });
    });
  }

  /* ================================================================== *
   * 4. Viewport animation gate — pause continuous loops off-screen
   * ------------------------------------------------------------------ *
   * Continuous decorative animations (the ticker track, the brand-marquee
   * track, lookbook hotspot pulses) opt in with [data-anim-pause]. While
   * the element sits outside the viewport it carries .ch-anim-offscreen,
   * and the owning section's CSS flips animation-play-state to paused —
   * so an idle page only runs the loops that are actually visible.
   *
   * Purely additive: without JS (or without IntersectionObserver) the
   * class never appears and every loop simply runs, exactly as before.
   * Reduced-motion users never reach here visually — the loops themselves
   * ship static prefers-reduced-motion fallbacks in their own CSS.
   * ================================================================== */
  let pauseObserver = null;

  function getPauseObserver() {
    if (!pauseObserver) {
      pauseObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            entry.target.classList.toggle('ch-anim-offscreen', !entry.isIntersecting);
          }
        },
        // A little headroom so loops resume just before they scroll back in.
        { rootMargin: '96px 0px' }
      );
    }
    return pauseObserver;
  }

  function initViewportPause(root = document) {
    if (!('IntersectionObserver' in window)) return;
    const targets = Array.from(root.querySelectorAll('[data-anim-pause]'));
    if (root instanceof Element && root.matches('[data-anim-pause]')) targets.unshift(root);
    if (!targets.length) return;
    const observer = getPauseObserver();
    // Re-observing an already-observed node is a spec-level no-op.
    targets.forEach((el) => observer.observe(el));
  }

  /* ================================================================== *
   * Public rescan hook — for runtime-injected .ch-reveal content
   * ------------------------------------------------------------------ *
   * Sections that append `.ch-reveal` nodes after boot (e.g. recently-viewed
   * fetching cards via the Section Rendering API) can't rely on the initial
   * observer scan, which already ran. They call window.chObserveReveals(root)
   * after injecting so the new targets get observed (or, under reduced motion,
   * marked in immediately). Idempotent: initScrollReveals skips already-in
   * nodes, so calling with the whole document is safe too.
   * ================================================================== */
  window.chObserveReveals = (root = document) => initScrollReveals(root);

  /* ================================================================== *
   * Boot
   * ------------------------------------------------------------------ *
   * Wrapped in try/catch: html.js-motion is added the moment this file
   * starts executing (step 0), which arms the CSS hidden-start state for
   * .ch-reveal / .masked-reveal__inner. If anything below throws before
   * the observer/session logic attaches, those elements would be stuck
   * hidden with nothing left to reveal them. The catch calls the same
   * reduced-motion-style "reveal everything now" fallback so a JS error
   * degrades to fully visible instead of permanently invisible.
   * ================================================================== */
  function revealEverythingNow() {
    document.querySelectorAll('.ch-reveal:not(.is-in)').forEach((el) => {
      el.classList.add('ch-reveal--in', 'is-in');
    });
    document.querySelectorAll('masked-reveal:not(.masked-reveal--in)').forEach((el) => {
      el.classList.add('masked-reveal--in');
      el.querySelectorAll('.masked-reveal__inner').forEach((inner) => {
        inner.style.transform = 'none';
        inner.style.animation = 'none';
      });
    });
  }

  function boot() {
    try {
      initScrollReveals();
      watchInjectedReveals();
      initViewTransitions();
      initViewportPause();
    } catch (err) {
      revealEverythingNow();
      throw err;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme editor: re-scan a section when it is added / reordered so reveals and
  // viewport pausing work live in the customizer (matches Dawn's designMode
  // re-init behaviour). The matching unload explicitly unobserves the removed
  // section's targets: observing is a no-op when repeated, but a long editor
  // session would otherwise accumulate detached nodes in the two shared
  // observers (they are never GC'd while still observed).
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      initScrollReveals(event.target);
      initViewportPause(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const root = event.target;
      if (!(root instanceof Element)) return;
      if (pauseObserver) {
        const targets = Array.from(root.querySelectorAll('[data-anim-pause]'));
        if (root.matches('[data-anim-pause]')) targets.unshift(root);
        for (const el of targets) pauseObserver.unobserve(el);
      }
      if (revealObserver) {
        const targets = Array.from(root.querySelectorAll(REVEAL_SELECTOR));
        if (root.matches(REVEAL_SELECTOR)) targets.unshift(root);
        for (const el of targets) revealObserver.unobserve(el);
      }
    });
  }
})();
